/**
 * SaaS plan marketing copy — Free (starter limits) · Pay as you go (₹1000) · Enterprise.
 * Mirrors API saas-self-serve.constants.js and public/prototypes/saas-self-serve.
 */

import { resolveBillingCurrency, resolveMinPaygUnlock } from "../utils/formatBillingMoney";

export const SAAS_FREE_GRANT = Object.freeze({
  credits: 400,
  avatarTemplateTries: 1,
});

/** Free plan metered pool: 200 account wallet + 100 per default actor. */
export const SAAS_FREE_CREDIT_POOL = Object.freeze({
  total: 400,
  accountWallet: 200,
  perDefaultActor: 100,
  actorAllocations: Object.freeze([
    Object.freeze({
      defaultKey: "default_male",
      actorName: "Default Male Actor",
      creditsAssigned: 100,
    }),
    Object.freeze({
      defaultKey: "default_female",
      actorName: "Default Female Actor",
      creditsAssigned: 100,
    }),
  ]),
});

/** First PAYG purchase funds default actors (200+200) from wallet debit. */
export const SAAS_DEFAULT_ACTOR_ASSIGNMENT = Object.freeze({
  grantCredits: 400,
  actorAllocations: Object.freeze([
    Object.freeze({
      defaultKey: "default_male",
      actorName: "Default Male Actor",
      creditsAssigned: 200,
    }),
    Object.freeze({
      defaultKey: "default_female",
      actorName: "Default Female Actor",
      creditsAssigned: 200,
    }),
  ]),
});

export const SAAS_MIN_PAYG_INR = 1000;
export const SAAS_MIN_PAYG_USD = 12;
export const SAAS_CREDITS_PER_INR = 4;

export const creditsForCommitmentInr = (amountInr) =>
  Math.max(0, Math.round(Number(amountInr) || 0) * SAAS_CREDITS_PER_INR);

export const SAAS_FREE_EXCEL_CAPS = Object.freeze({
  normalDrafts: null,
  renderedDrafts: null,
  personalizedCampaigns: null,
  publishedCampaigns: 1,
  personalizedVideoSec: 30,
  personalizedAudioSec: 30,
  retriesPerCampaign: 3,
  personalizedVideoGenerates: null,
  personalizedVideoMaxElements: 1,
  productVideoMaxPersonalizedAudio: 2,
  imagesMaxPersonalizedAudio: 2,
  pipMaxPersonalizedAudio: 1,
  framesMaxPersonalizedAudio: 1,
  bgMusicMaxPersonalizedAudio: 1,
  fontUploads: 2,
  deconstructLinks: 1,
  bulkImportJobs: 1,
  bulkImportFiles: 10,
  lmsAuthoring: 2,
});

export const SAAS_PRICING_PILLS = Object.freeze([
  "₹1 = 4 credits",
  "Crown = Pay as you go",
  "Free = starter limits",
]);

export const SAAS_BUY_CREDITS_PILLS = Object.freeze([
  "₹1 = 4 credits",
  `Minimum ₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")}`,
  "Lifetime unlock",
  "Card via Razorpay",
]);

export function buyCreditsPills({ currency = "INR", minUnlock, usdInrRate, isPremium = false } = {}) {
  const curr = String(currency || "INR").toUpperCase();
  const lockPill = isPremium ? "Credits to wallet" : "Lifetime unlock";
  if (curr === "USD") {
    const min = minUnlock != null ? minUnlock : SAAS_MIN_PAYG_USD;
    const rate = Number(usdInrRate);
    const perUsd = Number.isFinite(rate) && rate > 0 ? Math.round(rate * SAAS_CREDITS_PER_INR) : null;
    return [
      perUsd ? `$1 = ${perUsd.toLocaleString("en-US")} credits` : "Credits from live USD→INR (₹1 = 4)",
      `Minimum $${Number(min).toLocaleString("en-US")}`,
      lockPill,
      "Card via Razorpay",
    ];
  }
  if (isPremium) {
    return [
      "₹1 = 4 credits",
      `Minimum ₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")}`,
      lockPill,
      "Card via Razorpay",
    ];
  }
  return SAAS_BUY_CREDITS_PILLS;
}

/** Header / UpgradeDrawer CTA: first purchase vs later top-up. */
export function buyCreditsCtaLabel({ isPremium = false, minLabel } = {}) {
  return isPremium ? `Top up from ${minLabel}` : `Upgrade from ${minLabel}`;
}

/** Buy-credits hero: first PAYG unlock vs wallet recharge. */
export function buyCreditsHeroCopy({
  isPremium = false,
  isLoading = false,
  minUnlockLabel,
  explainer,
  currency = "INR",
} = {}) {
  const curr = String(currency || "INR").toUpperCase();
  if (isLoading && !isPremium) {
    return {
      eyebrow: "Credits",
      title: `Buy credits — ${minUnlockLabel} minimum`,
      lead: `Choose an amount. ${explainer}.`,
      amountLabel: `Amount (${curr})`,
    };
  }
  if (isPremium) {
    return {
      eyebrow: "Top up",
      title: `Top up credits — ${minUnlockLabel} minimum`,
      lead: `Choose an amount. ${explainer}. Credits add to your wallet after payment — Pay as you go stays unlocked.`,
      amountLabel: `Top-up amount (${curr})`,
    };
  }
  return {
    eyebrow: "Pay as you go",
    title: `Start Pay as you go — ${minUnlockLabel} minimum`,
    lead: `Choose a commitment amount. ${explainer}. First payment unlocks Pay as you go for life; Generate AI burns credits afterward.`,
    amountLabel: `Commitment amount (${curr})`,
  };
}

/** Legacy pack catalog (API backward-compat). Self-serve UI uses amount commitment instead. */
export const SAAS_CREDIT_PACKS = Object.freeze([
  {
    id: "topup1000",
    name: "₹1,000 credits",
    priceInr: 1000,
    listInr: 1000,
    discountPercent: 0,
    credits: 4000,
    badge: "Minimum",
    tagline: "Unlock Pay as you go · start generating",
    description:
      "First purchase unlocks Pay as you go features for life. Then burn credits only when you Generate AI.",
    audience: "First PAYG unlock",
    featured: true,
    primeFocus: ["PAYG unlock", "AI Generate", "Shopify"],
    valueAdds: [
      { label: "Lifetime Pay as you go unlock", note: "All self-serve features on" },
      { label: "4,000 credits", note: "₹1 = 4 · ₹1,000 list" },
      { label: "Templates + Smart Campaign", note: "Generate with wallet credits" },
    ],
    highlights: [
      "Minimum ₹1,000 commitment",
      "Credits = list ₹ × 4",
      "INR + GST · offer codes at checkout",
    ],
  },
  {
    id: "topup1660",
    name: "₹1,660 credits",
    priceInr: 1660,
    listInr: 1660,
    discountPercent: 0,
    credits: 6640,
    badge: "Popular",
    tagline: "More runway vs ₹1,000",
    description: "Same PAYG unlock (if first purchase) with more credit runway.",
    audience: "Active weekly creators",
    featured: false,
    primeFocus: ["2× credits", "Avatar", "LMS"],
    valueAdds: [
      { label: "6,640 credits", note: "₹1,660 list" },
      { label: "Everything in Pay as you go", note: "No feature gating by pack size" },
    ],
    highlights: [
      "Same path burn rates as all top-ups",
      "Apply LAUNCH20 / TRUSTVID10 before paying",
    ],
  },
  {
    id: "topup4150",
    name: "₹4,150 credits",
    priceInr: 3735,
    listInr: 4150,
    discountPercent: 10,
    credits: 16600,
    badge: "10% off",
    tagline: "Volume discount · more runway",
    description:
      "Pay ~10% less than list while receiving credits at full ₹4,150 list face (₹1 = 4).",
    audience: "Teams running regular campaigns",
    featured: false,
    primeFocus: ["Volume", "WhatsApp", "Analytics"],
    valueAdds: [
      { label: "16,600 credits", note: "From ₹4,150 list face" },
      { label: "~10% off payable", note: "Better ₹/credit vs ₹1,000" },
    ],
    highlights: [
      "List ₹4,150 · pay ₹3,735 after 10% volume discount",
      "Credits still = list × 4",
    ],
  },
  {
    id: "topup8300",
    name: "₹8,300 credits",
    priceInr: 7055,
    listInr: 8300,
    discountPercent: 15,
    credits: 33200,
    badge: "15% off",
    tagline: "Best self-serve value",
    description: "Largest top-up with ~15% volume discount on payable INR.",
    audience: "High-throughput Pay as you go accounts",
    featured: false,
    primeFocus: ["Best value", "High volume", "LMS"],
    valueAdds: [
      { label: "33,200 credits", note: "From ₹8,300 list face" },
      { label: "~15% off payable", note: "Best ₹/credit on self-serve" },
    ],
    highlights: [
      "List ₹8,300 · pay ₹7,055 after 15% volume discount",
      "Need committed volume SKUs? Contact Enterprise sales",
    ],
  },
]);

export const SAAS_COMMITMENT_BULLETS = Object.freeze([
  "Lifetime Pay as you go unlock on first successful payment ≥ ₹1,000",
  "Credits = amount × 4 (₹1 = 4 TrustVid Credits)",
  "Pay by card via Razorpay · INR + GST",
  "Generate AI burns credits; Preview stays free",
]);

export function commitmentBullets({ currency = "INR", minUnlock, explainer, isPremium = false } = {}) {
  const curr = String(currency || "INR").toUpperCase();
  if (isPremium) {
    if (curr === "USD") {
      return [
        "Credits add to your Pay as you go wallet",
        explainer || "Credits from live USD→INR (₹1 = 4)",
        "Pay by card via Razorpay",
        "Generate AI burns credits; Preview stays free",
      ];
    }
    return [
      "Credits add to your Pay as you go wallet",
      "Credits = amount × 4 (₹1 = 4 TrustVid Credits)",
      "Pay by card via Razorpay · INR + GST",
      "Generate AI burns credits; Preview stays free",
    ];
  }
  if (curr !== "USD") return SAAS_COMMITMENT_BULLETS;
  const min = minUnlock != null ? minUnlock : SAAS_MIN_PAYG_USD;
  const minLabel = `$${Number(min).toLocaleString("en-US")}`;
  return [
    `Lifetime Pay as you go unlock on first successful payment ≥ ${minLabel}`,
    explainer || "Credits from live USD→INR (₹1 = 4)",
    "Pay by card via Razorpay",
    "Generate AI burns credits; Preview stays free",
  ];
}

export const SAAS_PACK_ESTIMATE_PATHS = Object.freeze([
  { key: "avatar30s", short: "30s Avatar videos", credits: 1520 },
  { key: "avatar10s", short: "10s Avatar videos", credits: 560 },
  { key: "voice10s", short: "Audio personalization videos", credits: 120 },
  { key: "basic", short: "Basic personalised videos", credits: 80 },
  { key: "lmsVideo", short: "LMS interactive videos", credits: 100 },
]);

export const getSaasCreditPack = (packId) =>
  SAAS_CREDIT_PACKS.find((pack) => pack.id === packId) || SAAS_CREDIT_PACKS[0];

export const getPackEstimateLines = (credits) =>
  SAAS_PACK_ESTIMATE_PATHS.map((path) => ({
    key: path.key,
    short: path.short,
    count: Math.floor(Number(credits || 0) / path.credits),
  })).filter((line) => line.count > 0);

export const SAAS_PACK_FOOTNOTES = Object.freeze([
  "Pay as you go unlocks on first successful payment (≥ ₹1,000). Features stay on at ₹0 credits; Generate AI needs balance.",
  "Self-serve commitment: choose any amount ≥ ₹1,000. Credits = amount × 4.",
  "Free plan uses starter limits (unlimited drafts and renders, 1 published campaign, 30s personalization, 3 retries) — Generate until credits run out.",
]);

export function buyCreditsFootnotes({ isPremium = false, currency = "INR", minUnlock } = {}) {
  if (!isPremium) return SAAS_PACK_FOOTNOTES;
  const curr = String(currency || "INR").toUpperCase();
  if (curr === "USD") {
    const min = minUnlock != null ? minUnlock : SAAS_MIN_PAYG_USD;
    const minLabel = `$${Number(min).toLocaleString("en-US")}`;
    return [
      "Credits add to your wallet after payment. Pay as you go stays on at $0 credits; Generate AI needs balance.",
      `Self-serve top-up: choose any amount ≥ ${minLabel}. Credits from live USD→INR (₹1 = 4).`,
    ];
  }
  return [
    "Credits add to your wallet after payment. Pay as you go stays on at ₹0 credits; Generate AI needs balance.",
    "Self-serve top-up: choose any amount ≥ ₹1,000. Credits = amount × 4.",
  ];
}

export const SAAS_BILLING_FAQ = Object.freeze([
  {
    id: "free",
    title: "What is free?",
    body: "Free starter limits: unlimited drafts and renders · 1 published campaign, both default actors, 30s personalized video/audio, 3 retries per clip, Blank + Template create. Invite new actors, CRM, Analytics, Team are locked. No card to start.",
  },
  {
    id: "pay",
    title: "When do I pay?",
    body: `When you hit a Free limit or want Smart Campaign / Shopify / Actors. Commit from ₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")} via Razorpay (INR + GST).`,
  },
  {
    id: "rate",
    title: "₹1 = 4 TrustVid Credits",
    body: "Credits = commitment amount × 4. Face value: 1 credit = ₹0.25.",
  },
  {
    id: "burn",
    title: "What burns credits?",
    body: "On Pay as you go: Preview 0 · Basic 80 · Voice/sec 4 · Avatar/sec 48 · 10s Avatar 560 · 30s Avatar 1,520. Free uses starter limits instead of wallet burn.",
  },
  {
    id: "premium",
    title: "Lifetime Pay as you go unlock",
    body: `After the first successful payment (≥ ₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")}), features stay on even at ₹0 credits. Add credits to Generate AI.`,
  },
  {
    id: "v1",
    title: "What we don’t do in v1",
    body: "No monthly seats, no dynamic/surge pricing, and no self-serve Enterprise checkout. Volume SKUs are Contact sales only.",
  },
]);

export const SAAS_FREE_PLAN_FEATURES = Object.freeze([
  "Blank + Template Create Design",
  "Unlimited drafts · unlimited renders · 1 published campaign",
  "Both default actors · 30s video + 30s audio · 3 retries per clip",
  "Unlimited overlays · animations · preview",
  "Billing · TrustVid templates",
]);

export const SAAS_PREMIUM_PLAN_FEATURES = Object.freeze([
  "All Free capabilities without starter limits",
  "Smart Campaign · Travel itinerary · Deconstruct",
  "Shopify Create Design",
  "LMS + WhatsApp · Workflows · Journey Designer",
  "Full Analytics + Metering",
  "Actors library · unlimited personalization (credits)",
]);

export const SAAS_ENTERPRISE_PLAN_FEATURES = Object.freeze([
  "Volume video pricing (invoice)",
  "Master / brand-governed templates",
  "TrustVid CRM — sales-led",
]);

export const SAAS_FREE_CARD_LOCKED_FEATURES = Object.freeze(
  SAAS_PREMIUM_PLAN_FEATURES.slice(1, 5)
);

export const CREATE_DESIGN_ITINERARY_LABEL = "Travel itinerary";
export const CREATE_DESIGN_SMART_CAMPAIGN_LABEL = "Smart Campaign";

export const SAAS_CREATE_DESIGN_STARTERS = Object.freeze([
  { id: "blank", label: "Blank editor", premium: false, freePrimary: true },
  { id: "template", label: "Template", premium: false, freePrimary: true },
  { id: "import-json", label: "Import JSON", premium: false },
  { id: "itinerary", label: CREATE_DESIGN_ITINERARY_LABEL, premium: true },
  { id: "ai-campaign", label: CREATE_DESIGN_SMART_CAMPAIGN_LABEL, premium: true },
  { id: "designer-agent", label: "Designer Agent", premium: true },
  { id: "shopify", label: "Shopify Create Design", premium: true },
]);

export const SAAS_PLAN_TIERS = Object.freeze([
  {
    id: "free",
    name: "Free",
    badge: "NO CARD",
    badgeColor: "secondary",
    tagline: "Starter limits · guided workflow",
    priceMain: "₹0",
    priceHint: "to start",
    priceLabel: "₹0 to start",
    description:
      "Unlimited drafts and renders · 1 published campaign · both default actors · 30s personalization · 3 retries per clip. Blank + Template create. Crown-locked Pay as you go modules until you upgrade.",
    audience: "New accounts evaluating the product",
    ctaLabel: "Start free",
    ctaVariant: "primary",
    ctaAction: "startFree",
    featured: false,
    meta: [
      { dt: "Campaigns", dd: "Unlimited drafts · 1 published" },
      { dt: "Personalization", dd: "Until credits · 30s video + audio" },
      { dt: "Retries", dd: "3 per clip (1 + 2 regen)" },
    ],
    includedLabel: "Included · crown = Pay as you go",
  },
  {
    id: "premium",
    name: "Pay as you go",
    badge: "RECOMMENDED",
    badgeColor: "warning",
    tagline: `All features · buy credits from ₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")}`,
    priceMain: `From ₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")}`,
    priceHint: "min commitment",
    priceLabel: `From ₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")} min commitment`,
    description: `Commit any amount ≥ ₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")} to unlock Pay as you go for life. Credits = amount × 4 (₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")} → 4,000 credits).`,
    audience: "Self-serve teams generating AI video",
    ctaLabel: "Buy credits",
    ctaVariant: "warning",
    ctaAction: "buyCredits",
    featured: true,
    meta: [
      { dt: "Unlock", dd: "Lifetime Pay as you go" },
      { dt: "Min buy", dd: `₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")}` },
      { dt: "₹1 =", dd: "4 credits" },
    ],
    includedLabel: "Included",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    badge: "SALES-LED",
    badgeColor: "info",
    tagline: "Volume · brand · CRM",
    priceMain: "Custom",
    priceHint: "",
    priceLabel: "Custom",
    description:
      "Committed volume pricing, master templates, and TrustVid CRM. Contact sales — never Razorpay checkout.",
    audience: "Regulated & high-volume orgs",
    ctaLabel: "Talk to sales",
    ctaVariant: "primary",
    ctaAction: "enterprise",
    featured: false,
    meta: [
      { dt: "Checkout", dd: "Invoice only" },
      { dt: "Volume", dd: "Basic / Voice / Avatar" },
      { dt: "CRM", dd: "Sales-led" },
    ],
    includedLabel: "Included",
  },
]);

export const SAAS_COMPARE_ROWS = Object.freeze([
  {
    id: "credits",
    label: "Start / commitment",
    free: "Starter limits · no card",
    premium: `Buy ₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")}+ top-ups`,
    enterprise: "Custom volume",
  },
  {
    id: "avatar",
    label: "Personalized video",
    free: "1 × 30s + 30s audio · 3 retries",
    premium: "Unlimited (credits)",
    enterprise: "Committed volume",
  },
  {
    id: "ai",
    label: "Smart Campaign · Travel itinerary · Shopify",
    free: "crown",
    premium: "yes",
    enterprise: "Sales",
  },
  {
    id: "checkout",
    label: "Checkout",
    free: "—",
    premium: "Razorpay top-ups",
    enterprise: "Contact sales",
  },
]);

/** Maps API Free-cap / paywall codes to upgrade drawer copy. */
const _paygMin = `₹${SAAS_MIN_PAYG_INR.toLocaleString("en-IN")}`;
const _freeSec = `${SAAS_FREE_EXCEL_CAPS.personalizedVideoSec}s`;
const _freeRetries = SAAS_FREE_EXCEL_CAPS.retriesPerCampaign;
const _freeDrafts = SAAS_FREE_EXCEL_CAPS.normalDrafts;

export const SAAS_PAYWALL_COPY = Object.freeze({
  FREE_CREDITS_EXHAUSTED: {
    title: "Free credits used up",
    body: `Your Free plan includes 400 credits (200 account + 100 per default actor). Upgrade to Pay as you go from ${_paygMin} to add more.`,
  },
  FREE_ACTOR_CREDITS_EXHAUSTED: {
    title: "Recharge to add video entries",
    body: `This actor has no Free credits left. Upgrade to Pay as you go from ${_paygMin} to add more entries and generate videos.`,
  },
  ZERO_CREDITS: {
    title: "Add credits to Generate",
    body: `Preview stays free. Top up from ${_paygMin} to continue generating.`,
  },
  NO_CREDITS: {
    title: "Add credits to continue",
    body: `Your Pay as you go wallet is empty. Buy credits from ${_paygMin} to keep creating and generating.`,
  },
  FREE_CREATE_MODE_DENIED: {
    title: "Included in Pay as you go",
    body: `Smart Campaign, Travel itinerary, and Shopify unlock after your first ${_paygMin} top-up.`,
  },
  FREE_DRAFT_LIMIT: {
    title: "Free draft limit reached",
    body: _freeDrafts != null
      ? `Free allows ${_freeDrafts} draft campaigns. Upgrade to Pay as you go for more.`
      : "Upgrade to Pay as you go for more.",
  },
  FREE_RENDER_LIMIT: {
    title: "Free render limit reached",
    body: "Upgrade to Pay as you go to keep rendering.",
  },
  FREE_PERSONALIZE_CAMPAIGN_LIMIT: {
    title: "Free personalization limit reached",
    body: "Free allows personalization on 1 campaign. Upgrade to Pay as you go for more.",
  },
  FREE_PUBLISH_LIMIT: {
    title: "Free publish limit reached",
    body: `Free allows ${SAAS_FREE_EXCEL_CAPS.publishedCampaigns} published campaign. Upgrade to keep shipping videos.`,
  },
  FREE_DURATION_EXCEEDED: {
    title: `${_freeSec} Free personalization limit`,
    body: `Shorten personalized video/audio to ${_freeSec}, or upgrade for longer Generate.`,
  },
  FREE_RETRY_EXHAUSTED: {
    title: `${_freeRetries} Free retries used`,
    body: "This clip has used all Free generate attempts (1 generate + 2 regenerates). Upgrade to retry again.",
  },
  FREE_PV_GENERATE_EXHAUSTED: {
    title: "Free personalized video used",
    body: `Add credits or upgrade to Pay as you go from ${_paygMin} to keep generating personalized video.`,
  },
  FREE_FEATURE_DENIED: {
    title: "Pay as you go feature",
    body: `This module is not on the Free starter plan. Upgrade from ${_paygMin}.`,
  },
  FREE_PERSONALIZE_AUDIO_LIMIT: {
    title: "Free personalized audio limit",
    body: `Free limits how many clips can generate personalized audio. Upgrade from ${_paygMin} for more.`,
  },
  FREE_AUDIO_REQUIRED: {
    title: "Generate audio first",
    body: "Create personalized audio on this clip first, then generate video.",
  },
  FREE_FONT_UPLOAD_LIMIT: {
    title: "Font upload limit",
    body: `Free allows ${SAAS_FREE_EXCEL_CAPS.fontUploads} custom font uploads. Upgrade for more.`,
  },
  FREE_BULK_IMPORT_LIMIT: {
    title: "Bulk import limit",
    body: "Free allows 1 bulk import of up to 10 files.",
  },
  FREE_DECONSTRUCT_LIMIT: {
    title: "Deconstruct limit",
    body: "Free allows 1 deconstruct link. Upgrade for more.",
  },
  FREE_LMS_LIMIT: {
    title: "LMS Free limit",
    body: "Free allows limited LMS authoring. Upgrade for full interactive LMS.",
  },
  FREE_AVATAR_TRY_EXHAUSTED: {
    title: "Free personalized try used",
    body: "Upgrade to Pay as you go from ₹1,000 to keep generating.",
  },
  FREE_WHATSAPP_DENIED: {
    title: "WhatsApp delivery — Pay as you go",
    body: `Send videos on WhatsApp after upgrading from ${_paygMin}.`,
  },
  PAID_TEMPLATE_UPGRADE_REQUIRED: {
    title: "Paid template",
    body: `This template is included on Pay as you go. Preview stays open — upgrade from ${_paygMin} to Use template.`,
  },
  FREE_ACTOR_MUTATE_DENIED: {
    title: "Add, edit, or delete actors — Pay as you go",
    body: `Default actors are included on Free. Add, edit, or delete custom actors after upgrading from ${_paygMin}.`,
  },
});

export function getPaywallCopy(code, { currency = "INR", country, minUnlock } = {}) {
  const curr = resolveBillingCurrency({ currency, country });
  const min = resolveMinPaygUnlock({
    currency: curr,
    country,
    minPaygUnlock: minUnlock,
    minCommitment: minUnlock,
  });
  const minLabel = curr === "USD"
    ? `$${Number(min).toLocaleString("en-US")}`
    : `₹${Number(min).toLocaleString("en-IN")}`;
  const copy = SAAS_PAYWALL_COPY[code] || {
    title: "Upgrade to Pay as you go",
    body: `Unlock full TrustVid with a minimum ${minLabel} commitment.`,
  };
  if (curr !== "USD") {
    return copy;
  }
  return {
    title: copy.title,
    body: String(copy.body).replace(/₹1,000/g, minLabel),
  };
}
